// src/pages/Privacy.js
import React from "react";
import { useSEO } from "../hooks/useSEO";
import Card from "../components/ui/Card";

export default function Privacy() {
  useSEO({
    title: "Privacy Policy",
    description: "Read the Privacy Policy for Predict90. Learn how we collect, use, and protect your personal information.",
    keywords: "privacy policy, data protection, privacy, Predict90",
  });

  return (
    <div className="min-h-screen bg-gray-50 py-10 md:py-14">
      <div className="max-w-4xl mx-auto px-6">
        <Card>
          <div className="prose prose-sm md:prose-base max-w-none">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-sm text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                Predict90 ("we", "us", "our") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you use our Platform.
              </p>
              <p className="text-gray-700">
                By using the Platform, you consent to the data practices described in this Privacy Policy. 
                If you do not agree with the practices described, please do not use the Platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">2.1 Information You Provide</h3>
              <p className="text-gray-700 mb-4">We collect information that you provide directly to us:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li><strong>Account Information:</strong> Username, email address, password (hashed)</li>
                <li><strong>Profile Information:</strong> Display name, profile preferences</li>
                <li><strong>Predictions:</strong> Your match predictions and table predictions</li>
                <li><strong>Communication:</strong> Messages sent through contact forms or support channels</li>
                <li><strong>Payment information:</strong> The service is currently free. If we offer paid features later, payment details would be processed by third-party processors (we do not store full card numbers).</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">2.2 Automatically Collected Information</h3>
              <p className="text-gray-700 mb-4">We automatically collect certain information when you use the Platform:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent on Platform</li>
                <li><strong>Device Information:</strong> Device type, operating system, browser type</li>
                <li><strong>IP Address:</strong> Your IP address for security and analytics purposes</li>
                <li><strong>Cookies and Tracking:</strong> See Section 7 for details</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use the collected information for the following purposes:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li><strong>Platform Functionality:</strong> To provide, maintain, and improve the Platform</li>
                <li><strong>Account Management:</strong> To create and manage your account, authenticate users</li>
                <li><strong>Predictions & Scoring:</strong> To process your predictions, calculate scores, and maintain leaderboards</li>
                <li><strong>Communication:</strong> To send you notifications, updates, and respond to inquiries</li>
                <li><strong>Future payments:</strong> If optional paid features are introduced, to process those payments via third-party processors</li>
                <li><strong>Analytics:</strong> To analyze Platform usage and improve user experience</li>
                <li><strong>Security:</strong> To detect and prevent fraud, abuse, and security threats</li>
                <li><strong>Legal Compliance:</strong> To comply with legal obligations and enforce our Terms of Service</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. How We Share Your Information</h2>
              <p className="text-gray-700 mb-4">We do NOT sell your personal information. We may share your information in the following circumstances:</p>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">4.1 Public Information</h3>
              <p className="text-gray-700 mb-4">
                Your username and prediction scores are displayed on public leaderboards. This is necessary 
                for the competitive nature of the Platform.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">4.2 Service Providers</h3>
              <p className="text-gray-700 mb-4">
                We may share information with third-party service providers who perform services on our behalf:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li><strong>Payment processors (if applicable):</strong> If paid features are offered, to process payments (e.g., card networks via providers such as Stripe)</li>
                <li><strong>Email Services:</strong> To send notifications and communications (e.g., Brevo/Sendinblue)</li>
                <li><strong>Hosting Providers:</strong> To host and operate the Platform (e.g., Render, Vercel)</li>
                <li><strong>Analytics Services:</strong> To analyze Platform usage (if applicable)</li>
              </ul>
              <p className="text-gray-700">
                These service providers are contractually obligated to protect your information and use it 
                only for the purposes we specify.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">4.3 Legal Requirements</h3>
              <p className="text-gray-700 mb-4">
                We may disclose your information if required by law, court order, or government regulation, 
                or to protect our rights, property, or safety, or that of our users.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">4.4 Business Transfers</h3>
              <p className="text-gray-700">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred 
                to the acquiring entity, subject to the same privacy protections.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Storage and Security</h2>
              <p className="text-gray-700 mb-4">
                We implement appropriate technical and organizational measures to protect your information:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li><strong>Encryption:</strong> Passwords are hashed using secure algorithms</li>
                <li><strong>Secure Servers:</strong> Data is stored on secure servers with access controls</li>
                <li><strong>HTTPS:</strong> All data transmission is encrypted using SSL/TLS</li>
                <li><strong>Access Controls:</strong> Limited access to personal information on a need-to-know basis</li>
                <li><strong>Regular Updates:</strong> We keep our systems updated with security patches</li>
              </ul>
              <p className="text-gray-700">
                However, no method of transmission over the Internet or electronic storage is 100% secure. 
                While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your information for as long as necessary to provide the Platform and fulfill 
                the purposes outlined in this Privacy Policy, unless a longer retention period is required 
                by law.
              </p>
              <p className="text-gray-700 mb-4">
                When you delete your account, we will delete or anonymize your personal information, 
                except where we are required to retain it for legal or legitimate business purposes.
              </p>
              <p className="text-gray-700">
                Prediction data and leaderboard scores may be retained for historical and statistical 
                purposes even after account deletion.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar tracking technologies to enhance your experience:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li><strong>Essential Cookies:</strong> Required for Platform functionality (authentication, session management)</li>
                <li><strong>Analytics Cookies:</strong> To understand how users interact with the Platform</li>
                <li><strong>Preference Cookies:</strong> To remember your settings and preferences</li>
              </ul>
              <p className="text-gray-700 mb-4">
                You can control cookies through your browser settings. However, disabling certain cookies 
                may limit Platform functionality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Your Rights and Choices</h2>
              <p className="text-gray-700 mb-4">You have the following rights regarding your personal information:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and personal information</li>
                <li><strong>Opt-Out:</strong> Opt out of marketing communications (if applicable)</li>
                <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
              </ul>
              <p className="text-gray-700">
                To exercise these rights, please contact us at <a href="mailto:support@predict90.com" className="text-blue-600 hover:underline">support@predict90.com</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">9. GDPR Rights (For EU Users)</h2>
              <p className="text-gray-700 mb-4">
                If you are located in the European Economic Area (EEA), you have additional rights under 
                the General Data Protection Regulation (GDPR):
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">9.1 Legal Basis for Processing</h3>
              <p className="text-gray-700 mb-4">
                We process your personal information based on the following legal bases:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li><strong>Contract:</strong> To provide the Platform services you requested</li>
                <li><strong>Consent:</strong> Where you have given consent for specific processing activities</li>
                <li><strong>Legitimate Interests:</strong> To improve our services, ensure security, and prevent fraud</li>
                <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">9.2 Your GDPR Rights</h3>
              <p className="text-gray-700 mb-4">In addition to the rights listed in Section 8, EU users have the right to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li><strong>Right to Access:</strong> Obtain confirmation of whether we process your data and access to that data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete personal information</li>
                <li><strong>Right to Erasure ("Right to be Forgotten"):</strong> Request deletion of your personal information under certain circumstances</li>
                <li><strong>Right to Restrict Processing:</strong> Limit how we use your personal information</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured, commonly used format</li>
                <li><strong>Right to Object:</strong> Object to processing of your personal information for certain purposes</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">9.3 Exercising Your Rights</h3>
              <p className="text-gray-700 mb-4">
                To exercise any of these rights, please contact us at <a href="mailto:support@predict90.com" className="text-blue-600 hover:underline">support@predict90.com</a>. 
                We will respond to your request within 30 days.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">9.4 Right to Lodge a Complaint</h3>
              <p className="text-gray-700 mb-4">
                If you believe we have not addressed your concerns, you have the right to lodge a complaint 
                with your local data protection authority (supervisory authority). For EU users, you can 
                find your local authority at: <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://edpb.europa.eu/about-edpb/about-edpb/members_en</a>
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">9.5 Data Protection Officer</h3>
              <p className="text-gray-700">
                For GDPR-related inquiries, please contact us at <a href="mailto:support@predict90.com" className="text-blue-600 hover:underline">support@predict90.com</a>. 
                We will address your concerns in accordance with GDPR requirements.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Children's Privacy</h2>
              <p className="text-gray-700">
                The Platform is not intended for users under 18 years of age. We do not knowingly collect 
                personal information from children. If you believe we have collected information from a child, 
                please contact us immediately, and we will delete such information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">11. International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                Your information may be transferred to and processed in countries other than your country 
                of residence. These countries may have different data protection laws.
              </p>
              <p className="text-gray-700">
                By using the Platform, you consent to the transfer of your information to these countries. 
                We take appropriate measures to ensure your information is protected in accordance with this 
                Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. Material changes will be notified via 
                email or Platform notification. The "Last updated" date at the top indicates when this Policy 
                was last revised.
              </p>
              <p className="text-gray-700">
                Your continued use of the Platform after changes constitutes acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or your personal 
                information, please contact us:
              </p>
              <p className="text-gray-700">
                Email: <a href="mailto:support@predict90.com" className="text-blue-600 hover:underline">support@predict90.com</a>
              </p>
            </section>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                By using Predict90, you acknowledge that you have read and understood 
                this Privacy Policy and consent to the collection and use of your information as described herein.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
